import { Skeleton } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { debounce } from 'lodash';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useRecoilState, useRecoilValue } from 'recoil';
import slugify from 'slugify';

import PaginatedOrangesBookSlider from '@/components/BookComp/PaginatedOrangesBookSlider';

import { programmaticScrollTo } from '@/utils/ScrollControl/scrollControl';
import useHiddenCategories from '@/utils/useHiddenCategories';

import { tokenUpdate } from '../../atoms/authModalControl';
import { user } from '../../atoms/user';
import { ShouldRender } from '../../components/shared/basic/ShouldRender';
import { formateErrorObject } from '../../components/shared/formateErrorObject';
import { getLoginToken, setBookToken } from '../../components/shared/getToken';
import { notify } from '../../components/shared/notify';
import HeroSection from '../../components/V2/HomePage/HeroSection';
import HomepageSlider from '../../components/V2/HomePage/HomepageSlider';
import { EventDetails, logEvent } from '../../hooks/useClickEventTracking';
import { getBookToken } from '../../services/Library/Books';
import { getOrangesCategories } from '../../services/Library/Oranges';
import { User } from '../../utils/interfaces/User';

const Books: React.FC = () => {
  const [isTokenChanged, setIsTokenChanged] = useRecoilState(tokenUpdate);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [, setSearch] = useState<string>();
  const [token, setToken] = useState('');

  const router = useRouter();
  const [categories, setCategories] =
    useState<{ id: string; name: string }[]>();
  const [loadedCategories, setLoadedCategories] = useState<
    { categoryId: string; categoryName: string }[]
  >([]);
  const [currentFilter, setCurrentFilter] = useState<{
    name: string;
    value: string;
  }>({ name: '', value: '' });
  const [startIndex, setStartIndex] = useState(0);
  const [loadingV2, setLoadingV2] = useState<boolean>(false);
  const { filter, view } = router.query;
  const hiddenCategories = useHiddenCategories();

  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: false,
    rootMargin: '100px 0px',
  });

  const userData: User | undefined = useRecoilValue(user);
  const keyWord = router.query.search;
  const screens = useBreakpoint();

  const handleGetCategories = async () => {
    try {
      const response = await getOrangesCategories();
      const list = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];

      setCategories(
        list.map((category: any) => ({
          id: category?._id,
          name: category?.name,
        })),
      );
    } catch (error: any) {
      notify(formateErrorObject(error.message), 'error');
    }
  };

  const handleGetBookToken = async () => {
    if (token) {
      try {
        const response = await getBookToken();
        await setBookToken(response?.data?.file_token);
      } catch (error) {
        console.error('API call error:', error);
      }
    }
  };

  const fetchData = async () => {
    try {
      const token = await getLoginToken();
      setToken(token as string);
    } catch (error) {
      console.error('Error fetching token:', error);
    }
  };

  useEffect(() => {
    handleGetCategories();

    fetchData();
    handleGetBookToken();
    if (isTokenChanged) {
      setIsTokenChanged(false);
    }
  }, [isTokenChanged, token]);

  useEffect(() => {
    setSearchKeyword(keyWord as string);
  }, [keyWord]);

  const categoriesDataFormatted = useMemo(() => {
    return (
      categories
        ?.filter(
          (item) => !hiddenCategories.includes(item?.name?.toLowerCase()),
        )
        .map((item) => ({
          ...item,
          id: item?.id || '',
          name: item?.name || '',
        })) || []
    );
  }, [categories, hiddenCategories]);

  const delayedSearch = debounce((keyword: string) => {
    setSearch(keyword);
    if (keyWord) {
      const eventParams: EventDetails = {
        event_name: 'search_keyword',
        source: 'books',
        source_type: 'search',
        source_id: null,
        sub_source: null,
        sub_source_id: null,
        unit: 'string',
        value: keyword,
      };

      logEvent({
        eventDetails: eventParams,
        userData,
        pathname: router.pathname,
      });
    }
  }, 500);

  useEffect(() => {
    delayedSearch(searchKeyword);
    return () => {
      delayedSearch.cancel();
      setSearch(undefined);
    };
  }, [searchKeyword]);

  const loadCategory = async () => {
    await new Promise((resolve) => setTimeout(resolve, 200));
    const categoriesDataFormattedWithoutFirst = categoriesDataFormatted;
    const endIndex = Math.min(
      startIndex + 2,
      categoriesDataFormattedWithoutFirst?.length ?? 0,
    );
    if (
      !categoriesDataFormattedWithoutFirst ||
      startIndex >= categoriesDataFormattedWithoutFirst.length
    ) {
      setLoadingV2(false);
      return;
    }

    const categoriesToLoad = categoriesDataFormattedWithoutFirst.slice(
      startIndex,
      endIndex,
    );

    setLoadedCategories((prevLoadedCategories) => [
      ...prevLoadedCategories,
      ...categoriesToLoad.map((_, index) => ({
        categoryId: categoriesToLoad[index]?.id || '',
        categoryName: categoriesToLoad[index]?.name || '',
      })),
    ]);

    setStartIndex(endIndex);
    setLoadingV2(false);
  };



  const removeObjectsWithSameCategoryId = (
    objects: { categoryId: string; categoryName: string }[],
  ): { categoryId: string; categoryName: string }[] => {
    const categoryIds: Set<string> = new Set();
    const filteredObjects: { categoryId: string; categoryName: string }[] = [];

    for (const obj of objects) {
      if (!categoryIds.has(obj.categoryId)) {
        filteredObjects.push(obj);
        categoryIds.add(obj.categoryId);
      }
    }

    return filteredObjects;
  };

  useEffect(() => {
    if (inView) {
      const categoriesDataFormattedWithoutFirst =
        categoriesDataFormatted?.slice(1);
      if (
        !(
          !categoriesDataFormattedWithoutFirst ||
          startIndex >= categoriesDataFormattedWithoutFirst.length
        )
      ) {
        setLoadingV2(true);
      }

      loadCategory();
    }
  }, [inView, categoriesDataFormatted, startIndex]);

  useEffect(() => {
    if (filter && router.isReady && categoriesDataFormatted) {
      const foundCategory = categoriesDataFormatted.find(
        (category) => category.name === filter.toString(),
      );

      if (foundCategory) {
        setCurrentFilter({ name: foundCategory.name, value: foundCategory.id });
      }
    }
  }, [router.isReady, filter, categoriesDataFormatted]);

  const handleScrollToBottom = () => {
    if (typeof window !== 'undefined') {
      const target = document.getElementById('filtered-container');
      if (target) {
        const targetPosition =
          target.getBoundingClientRect().top + window.scrollY - 100;
        programmaticScrollTo({
          top: targetPosition,
          behavior: 'smooth',
        });
      }
    }
  };

  useEffect(() => {
    handleScrollToBottom();
  }, [currentFilter]);



  return (
    <>
      <Head>
        <title>
          Explore Finance Books at ZebraLearn: Your Online Bookstore
        </title>
        <meta
          name='description'
          content='Discover best books publisher in India specializing in non-fiction books. ZebraLearn offers cutting-edge design & technology insights, simplifying complex topics.
          '
          key='description'
        />
      </Head>
      <div className='flex flex-col'>
        <HeroSection />

        {/* <div>
          <PaginatedOrangesBookSlider
            categoryId={null}
            categoryName='Oranges'
            isHomePage={true}
            bookType='paperBack'
          />
        </div> */}

        <div className='flex flex-col '>
          <ShouldRender check={!searchKeyword?.length && !currentFilter.value}>
            <div>
              <div className='flex flex-col'>
                {removeObjectsWithSameCategoryId(loadedCategories)?.map(
                  (
                    categoryBook: {
                      categoryId: string;
                      categoryName: string;
                    },
                    index: number,
                  ) => {
                    const slug = slugify(categoryBook.categoryName || '', {
                      lower: true,
                    });
                    return (
                      <span key={`${categoryBook.categoryId}-${index}`}>
                        <PaginatedOrangesBookSlider
                          categoryId={categoryBook.categoryId.toString()}
                          categoryName={categoryBook.categoryName}
                          subHeading='Explore All'
                          subHeadingLink={`/oranges/shop/${slug}`}
                          hideShortBook={view?.toString() === 'cred'}
                          bookType='paperBack'
                          hideBars={true}
                        />
                      </span>
                    );
                  },
                )}
                <div ref={ref} style={{ height: 1, marginTop: '100px' }}></div>
              </div>

              {loadingV2 && (
                <HomepageSlider heading='' hideBars={true}>
                  {Array.from({ length: 10 }).map((_, index) => (
                    <span
                      key={index}
                      className='group cursor-pointer p-1 md:p-1.5 bg-transparent no-select '
                    >
                      <Skeleton.Button
                        style={{
                          width: screens.xl
                            ? '200px'
                            : screens.lg
                              ? '150px'
                              : '120px',
                          height: screens.xl
                            ? '200px'
                            : screens.lg
                              ? '150px'
                              : '120px',
                        }}
                        active={true}
                        className='rounded-sm md:rounded-md transition-transform transform hover:scale-[1.03] duration-500 max-w-[120px] lg:max-w-[140px] xl:max-w-[200px]'
                      />
                    </span>
                  ))}
                </HomepageSlider>
              )}
            </div>
          </ShouldRender>
        </div>
      </div>
    </>
  );
};

export default Books;
